import { useEffect, useId, useMemo, useRef, useState, type JSX, type KeyboardEvent, type RefObject } from 'react'
import { ApiError, type Tally } from '../lib/api'
import {
  actGroups,
  actStep,
  actStop,
  actsSummary,
  auditChips,
  toggleAct,
  zoneName,
  zoneWord,
  NO_CHOICES,
  type AuditChoices,
} from '../lib/auditFilter'
import { auditLabel } from '../lib/auditLabels'
import { Icon } from './ui'
import '../styles/lists.css'

// The Audit trail's filter, as one row above the trail.
//
// # WHY THE ACTS ARE BEHIND A CONTROL
//
// A chain that has been written to for a month holds well over a hundred kinds
// of act. Laid out as chips they were the whole first screen, and the trail,
// which is what the reader came for, began below the fold (the founder's
// review of 11 October 2026). Choosing acts is ten seconds of the visit, so it
// opens from one control, grouped as the product is and narrowed by typing.
//
// # WHAT STAYS ON THE BAR
//
// Everything the trail is narrowed by, as a chip that removes it, and how many
// acts are ticked, on the control itself. A closed list with a tick inside it
// is a filter nobody can see, and on this screen an unseen filter reads as
// "this person did nothing that week".
//
// # WHY THERE IS NO APPLY
//
// One read of the trail is one indexed page of fifty, so a choice is read as
// it is made. The typed boxes wait for the typing to pause (the screen decides
// how long), because the API matches an actor and a record exactly and half an
// address matches nothing. Enter reads at once.

export function AuditFilters({
  choices,
  applied,
  onChoose,
  onType,
  actions,
  actionsError,
  onReadActions,
  seenActors,
  seenTypes,
}: {
  // What the controls hold, and what the trail on the screen was read with.
  // They differ only while somebody is typing.
  choices: AuditChoices
  applied: AuditChoices
  // A choice to read now, and one to read when the typing pauses.
  onChoose: (next: AuditChoices) => void
  onType: (next: AuditChoices) => void
  // null until the vocabulary read answers, which is not the empty list.
  actions: Tally[] | null
  actionsError: unknown
  onReadActions: () => void
  // Offered under the boxes: what the rows read so far hold.
  seenActors: string[]
  seenTypes: string[]
}): JSX.Element {
  const chips = auditChips(applied)
  const zone = zoneName()
  const zoneShort = zoneWord()
  const now = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onChoose(choices)
  }
  // A chip is drawn from what was read, and taken off what the controls hold
  // now, so removing one does not also drop a word typed since.
  const without = (key: string) => {
    const at = auditChips(choices).find((c) => c.key === key)
    onChoose(at ? at.without : choices)
  }

  return (
    <div className="audit-bar" role="search" aria-label="Narrow the trail">
      <div className="audit-bar-row">
        <ActsMenu
          actions={actions}
          error={actionsError}
          chosen={choices.acts}
          onToggle={(key) => onChoose(toggleAct(choices, key))}
          onClear={() => onChoose({ ...choices, acts: [] })}
          onRead={onReadActions}
        />

        <div className="field audit-bar-text">
          <label htmlFor="audit-actor">Actor</label>
          <input
            id="audit-actor"
            type="search"
            list="audit-actors"
            value={choices.actor}
            placeholder="ciso@example.com"
            spellCheck={false}
            autoComplete="off"
            enterKeyHint="search"
            onChange={(e) => onType({ ...choices, actor: e.target.value })}
            onKeyDown={now}
          />
          <datalist id="audit-actors">
            {seenActors.map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </div>

        <div className="field audit-bar-kind">
          <label htmlFor="audit-entity-type">Kind of record</label>
          <input
            id="audit-entity-type"
            type="search"
            list="audit-entity-types"
            value={choices.entityType}
            placeholder="vendor"
            spellCheck={false}
            autoComplete="off"
            enterKeyHint="search"
            onChange={(e) => onType({ ...choices, entityType: e.target.value })}
            onKeyDown={now}
          />
          <datalist id="audit-entity-types">
            {seenTypes.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>

        <div className="field audit-bar-text">
          <label htmlFor="audit-entity-id">Record</label>
          <input
            id="audit-entity-id"
            type="search"
            className="mono"
            value={choices.entityId}
            placeholder="identifier, as in the table"
            spellCheck={false}
            autoComplete="off"
            enterKeyHint="search"
            onChange={(e) => onType({ ...choices, entityId: e.target.value })}
            onKeyDown={now}
          />
        </div>

        {/* A date is a day on the reader's own clock, from its midnight to the
            next, and the API is sent those instants. The control says whose
            day it means: the zone beside the label, and by its full name in
            the sentence that describes the control. */}
        <div className="field audit-bar-date">
          <label htmlFor="audit-since">
            On or after <span className="audit-bar-zone">({zoneShort})</span>
          </label>
          <input
            id="audit-since"
            type="date"
            value={choices.since}
            aria-describedby="audit-day-hint"
            title={'A day on this device\'s clock, ' + zone}
            max={choices.until || undefined}
            onChange={(e) => onType({ ...choices, since: e.target.value })}
            onKeyDown={now}
          />
        </div>

        <div className="field audit-bar-date">
          <label htmlFor="audit-until">
            On or before <span className="audit-bar-zone">({zoneShort})</span>
          </label>
          <input
            id="audit-until"
            type="date"
            value={choices.until}
            aria-describedby="audit-day-hint"
            title={'A day on this device\'s clock, ' + zone}
            min={choices.since || undefined}
            onChange={(e) => onType({ ...choices, until: e.target.value })}
            onKeyDown={now}
          />
        </div>
      </div>

      <span className="vh" id="audit-day-hint">
        A day here is a day on this device's own clock, {zone}: from its midnight to the next.
      </span>

      {chips.length > 0 && (
        <div className="audit-bar-chips" data-testid="audit-chips">
          <span className="vh">The trail is narrowed by</span>
          {chips.map((c) => (
            <button
              type="button"
              className="filterbar-chip"
              key={c.key}
              title={c.title}
              aria-label={'Remove ' + c.label}
              onClick={() => without(c.key)}
            >
              {c.label}
              <span aria-hidden="true">×</span>
            </button>
          ))}
          <button type="button" className="filterbar-clear" onClick={() => onChoose(NO_CHOICES)}>
            Clear all
          </button>
        </div>
      )}
    </div>
  )
}

// The Acts control and the list it opens: whether the list is open, and what
// has to happen in the page when it opens. What is drawn, and what each key
// and press does, is ActsMenuView, which a test can draw open or closed.
type ActsMenuProps = {
  actions: Tally[] | null
  error: unknown
  chosen: string[]
  onToggle: (key: string) => void
  onClear: () => void
  onRead: () => void
}

function ActsMenu(props: ActsMenuProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const opener = useRef<HTMLButtonElement>(null)
  const find = useRef<HTMLInputElement>(null)
  const id = useId()

  useEffect(() => {
    if (!open) return
    find.current?.focus()
    // On a phone the bar is half way down the first screen, under the menu,
    // and the list would run off the bottom of the window with Clear and Done
    // on it. The control is brought to the top so the list has the window.
    const panel = document.getElementById(id + '-panel')
    if (panel && panel.getBoundingClientRect().bottom > window.innerHeight) wrap.current?.scrollIntoView({ block: 'start' })
    const away = (e: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    return () => document.removeEventListener('pointerdown', away)
  }, [open, id])

  return <ActsMenuView {...props} id={id} open={open} setOpen={setOpen} wrap={wrap} opener={opener} find={find} />
}

export function ActsMenuView({
  id,
  open,
  setOpen,
  wrap,
  opener,
  find,
  actions,
  error,
  chosen,
  onToggle,
  onClear,
  onRead,
}: ActsMenuProps & {
  id: string
  open: boolean
  setOpen: (open: boolean) => void
  wrap?: RefObject<HTMLDivElement | null>
  opener?: RefObject<HTMLButtonElement | null>
  find?: RefObject<HTMLInputElement | null>
}): JSX.Element {
  // Closing by Escape or by Done hands the focus back to the control that
  // opened the list. Left where it was, the focus would be on a button that
  // has just gone, which is nowhere.
  const close = () => {
    setOpen(false)
    opener?.current?.focus()
  }

  // A real label, so it is set as the labels of the boxes beside it are. The
  // control's name is the label and what it says: "Acts, 2 chosen".
  const label = (
    <label id={id + '-label'} htmlFor={id + '-open'}>
      Acts
    </label>
  )

  // The read failed. Said on the bar, in words, because the control it would
  // have filled is the only place the reader would otherwise look for it: an
  // empty list here once read as a tenant whose chain held nothing.
  if (error != null) {
    return (
      <div className="field audit-bar-acts">
        {label}
        <button
          id={id + '-open'}
          type="button"
          className="filterbar-open audit-acts-failed"
          aria-labelledby={id + '-label ' + id + '-open'}
          title={error instanceof ApiError ? error.message : undefined}
          data-testid="audit-acts-failed"
          onClick={onRead}
        >
          <Icon name="alert" size={14} /> Not read, try again
        </button>
      </div>
    )
  }

  const none = actions !== null && actions.length === 0
  return (
    <div
      className="field audit-bar-acts"
      ref={wrap}
      // Every place the focus can be while the list is open is inside this
      // (the list holds its own focus when its padding is pressed), so the
      // key is heard here. Stopped here, so that one press closes this list
      // and nothing behind it.
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !open) return
        e.preventDefault()
        e.stopPropagation()
        close()
      }}
      onBlur={(e) => {
        // Tabbing past the last button leaves the list; it does not stay open
        // over the trail with the focus somewhere else.
        const to = e.relatedTarget as Node | null
        if (open && to && !e.currentTarget.contains(to)) setOpen(false)
      }}
    >
      {label}
      <button
        id={id + '-open'}
        ref={opener}
        type="button"
        className={'filterbar-open' + (chosen.length > 0 ? ' on' : '')}
        aria-labelledby={id + '-label ' + id + '-said'}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id + '-panel' : undefined}
        disabled={actions === null || none}
        data-testid="audit-acts-open"
        onClick={() => setOpen(!open)}
      >
        <span id={id + '-said'}>{actions === null ? 'Reading' : none ? 'None recorded' : actsSummary(chosen.length)}</span>
        <span className="audit-acts-caret" aria-hidden="true">
          <Icon name="chevron" size={14} />
        </span>
      </button>

      {open && actions && <ActsList id={id} actions={actions} chosen={chosen} find={find} onToggle={onToggle} onClear={onClear} onDone={close} />}
    </div>
  )
}

// The list the Acts control opens: every act the chain holds, under the
// product's groups, each with how often it has been recorded, narrowed by
// typing. What is typed lives here, so it is gone when the list closes and
// each opening starts from the whole list.
//
// It can hold the focus itself (tabIndex -1). The page around it can too, so a
// press on the list's own padding or a group's name would otherwise move the
// focus out to the page, and a list that closes when the focus leaves would
// close under the pointer.
export function ActsList({
  id,
  actions,
  chosen,
  find,
  onToggle,
  onClear,
  onDone,
}: {
  id: string
  actions: Tally[]
  chosen: string[]
  // The box that takes the focus when the list opens.
  find?: RefObject<HTMLInputElement | null>
  onToggle: (key: string) => void
  onClear: () => void
  onDone: () => void
}): JSX.Element {
  const [typed, setTyped] = useState('')
  const groups = useMemo(() => actGroups(actions, typed), [actions, typed])
  // The act that holds the list's one stop in the tab order.
  const [last, setLast] = useState<string | null>(null)
  const stop = actStop(
    groups.flatMap(([, acts]) => acts.map((a) => a.key)),
    last,
  )
  const list = useRef<HTMLDivElement>(null)
  const actButtons = () => [...(list.current?.querySelectorAll<HTMLButtonElement>('button[data-act]') ?? [])]
  const move = (e: KeyboardEvent<HTMLDivElement>) => {
    // With a modifier the arrows are the browser's, as on the tab rows.
    if (e.altKey || e.metaKey || e.ctrlKey) return
    const all = actButtons()
    const to = actStep(
      e.key,
      all.indexOf(document.activeElement as HTMLButtonElement),
      all.map((b) => b.getBoundingClientRect()),
    )
    if (to < 0) return
    e.preventDefault()
    all[to]?.focus()
  }
  return (
    <div className="filterbar-panel audit-acts-panel" id={id + '-panel'} role="dialog" aria-label="Acts to show" tabIndex={-1}>
      <label className="vh" htmlFor={id + '-find'}>
        Find an act
      </label>
      <input
        id={id + '-find'}
        ref={find}
        type="search"
        className="audit-acts-find"
        placeholder="Find an act"
        value={typed}
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => {
          // Down from the box goes into the acts, at the one that Tab would
          // have reached.
          if (e.key !== 'ArrowDown') return
          e.preventDefault()
          actButtons()
            .find((b) => b.tabIndex === 0)
            ?.focus()
        }}
      />

      <div className="audit-acts-list" ref={list} onKeyDown={move}>
        {groups.map(([group, acts]) => (
          <div className="filterbar-group" key={group} role="group" aria-labelledby={id + '-g-' + group}>
            <h3 id={id + '-g-' + group}>{group}</h3>
            <div className="filterbar-options">
              {acts.map((a) => {
                const on = chosen.includes(a.key)
                return (
                  <button
                    type="button"
                    key={a.key}
                    className={'filterbar-option' + (on ? ' on' : '')}
                    aria-pressed={on}
                    // The figure alone is read out as a bare number after the
                    // act's name; this says what it counts.
                    aria-label={auditLabel(a.key) + ', recorded ' + a.count + (a.count === 1 ? ' time' : ' times')}
                    title={a.key}
                    data-act={a.key}
                    tabIndex={a.key === stop ? 0 : -1}
                    onFocus={() => setLast(a.key)}
                    onClick={() => onToggle(a.key)}
                  >
                    {auditLabel(a.key)}
                    <span className="audit-acts-count">{a.count}</span>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
        {groups.length === 0 && <p className="muted audit-acts-nomatch">No act this chain holds matches {typed.trim()}</p>}
      </div>

      <div className="filterbar-foot">
        <span className="audit-acts-said" role="status">
          {chosen.length} of {actions.length} chosen
        </span>
        <button
          type="button"
          className="ghost"
          disabled={chosen.length === 0}
          onClick={() => {
            // Clearing disables this button, and a disabled button drops the
            // focus onto the page. It goes to the box instead.
            find?.current?.focus()
            onClear()
          }}
        >
          Clear
        </button>
        <button type="button" className="primary" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  )
}
